import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@ohpen/enso-ui';

import { ConfigTableActions } from '@config/components';
import { useConfigContext } from '@config/context';
import { CONFIG_PAGINATION, CONFIG_SCROLL } from '@config/utils';
import type { CustomFieldRow } from '../interfaces';
import { CUSTOM_FIELD_TYPES, customFieldColumns, ENTITY_LIST_NAME, TYPE_LIST_NAME } from '../tableConfig';

export const CustomFieldsList = (): ReactElement => {
  const { t } = useTranslation();
  const { configuration, customFields } = useConfigContext();

  const dataSource: CustomFieldRow[] = useMemo(() => {
    const entries: CustomFieldRow[] =
      customFields ?? (configuration?.additionalFields ?? []).map(field => ({ code: field.code, name: field.name ?? field.code }));

    return [...entries].sort((a, b) => (a.entityLabel ?? '').localeCompare(b.entityLabel ?? '') || a.code.localeCompare(b.code));
  }, [configuration, customFields]);

  const valueLists = useMemo(() => {
    const entities: Record<string, string> = {};
    dataSource.forEach(row => {
      if (row.entity) entities[row.entity] = row.entityLabel ?? row.entity;
    });

    const types = Object.fromEntries(CUSTOM_FIELD_TYPES.map(type => [type, t(`config.customFields.types.${type}`)]));

    return { [ENTITY_LIST_NAME]: entities, [TYPE_LIST_NAME]: types };
  }, [dataSource, t]);

  return (
    <Table
      id="configCustomFields"
      rowKey="code"
      dataSource={dataSource}
      columns={customFieldColumns}
      valueLists={valueLists}
      pagination={CONFIG_PAGINATION}
      scroll={CONFIG_SCROLL}
      itemsLabel={t('config.sections.customFields')}
    >
      <ConfigTableActions />
    </Table>
  );
};
